import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProductThumb({ product }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const img = product.images?.[0];
    if (!img) return;
    const imgData = typeof img === 'string' ? { storage_path: img } : img;
    if (imgData.external_url) { setSrc(imgData.external_url); return; }
    if (!imgData.storage_path) return;
    let alive = true;
    axios.get(`${API}/files/${imgData.storage_path}`, { responseType: 'blob' })
      .then(r => { if (alive) setSrc(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [product]);
  return (
    <div className="w-14 h-14 rounded-lg bg-[#F0EBE1] overflow-hidden flex-shrink-0">
      {src && <img src={src} alt={product.title} className="w-full h-full object-cover" />}
    </div>
  );
}

export default function RecoveryDialog({ open, onOpenChange, getHeaders, onMigrated }) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    axios.get(`${API}/flyers/recovery/available-products`, { withCredentials: true, headers: getHeaders() })
      .then(r => setProducts(r.data.products || []))
      .catch(() => toast.error('Errore caricamento recupero'))
      .finally(() => setLoading(false));
  }, [open, getHeaders]);

  const toggle = (id) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map(p => p.id)));
  };

  const migrate = async () => {
    if (selected.size === 0) return;
    setMigrating(true);
    try {
      const { data } = await axios.post(
        `${API}/flyers/recovery/migrate`,
        { product_ids: Array.from(selected) },
        { withCredentials: true, headers: getHeaders() }
      );
      toast.success(`${data.migrated} prodotti spostati al volantino di oggi`);
      if (data.flyers_auto_deleted?.length > 0) {
        toast.info(`${data.flyers_auto_deleted.length} volantino/i vecchio/i svuotato/i ed eliminato/i`);
      }
      onMigrated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore migrazione');
    } finally { setMigrating(false); }
  };

  const byFlyer = {};
  products.forEach(p => {
    const key = p.from_flyer_date || 'altro';
    (byFlyer[key] = byFlyer[key] || []).push(p);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="recovery-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>
            🔄 Recupera prodotti dai vecchi volantini
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-[#C25934]" />
          </div>
        ) : products.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
            <span className="text-4xl mb-3">🎉</span>
            <p className="font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
              Nessun prodotto da recuperare
            </p>
            <p className="text-xs text-[#877E78] mt-1">
              Tutti i prodotti dei vecchi volantini sono già esauriti o già nel volantino di oggi.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-1 pb-2 border-b border-[#E8E3D9]">
              <p className="text-xs text-[#877E78]">
                <b>{products.length}</b> prodotti ancora disponibili · <b className="text-[#C25934]">{selected.size}</b> selezionati
              </p>
              <button
                data-testid="recovery-toggle-all"
                onClick={toggleAll}
                className="text-xs text-[#C25934] font-medium hover:underline"
              >
                {selected.size === products.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 py-2">
              {Object.entries(byFlyer).map(([date, pList]) => (
                <div key={date}>
                  <p className="text-[10px] font-bold text-[#877E78] uppercase tracking-wider px-1 py-1">
                    Volantino del {date} ({pList.length})
                  </p>
                  <div className="space-y-1.5">
                    {pList.map(p => {
                      const isSel = selected.has(p.id);
                      const stock = p.stock || {};
                      let availLabel = 'Disponibile';
                      if (stock.track_inventory) {
                        if (stock.per_variant?.length > 0) {
                          const total = stock.per_variant.reduce((s, v) => s + (v.qty || 0), 0);
                          availLabel = `${total} pz (${stock.per_variant.length} varianti)`;
                        } else {
                          availLabel = `${stock.total || 0} pz`;
                        }
                      }
                      return (
                        <button
                          key={p.id}
                          data-testid={`recovery-product-${p.id}`}
                          onClick={() => toggle(p.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors text-left ${
                            isSel ? 'border-[#C25934] bg-[#C25934]/5' : 'border-[#E8E3D9] hover:bg-[#F0EBE1]'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                            isSel ? 'bg-[#C25934] border-[#C25934]' : 'border-[#E8E3D9]'
                          }`}>
                            {isSel && <Check size={12} className="text-white" strokeWidth={3} />}
                          </div>
                          <ProductThumb product={p} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2A2624] truncate">{p.title}</p>
                            <p className="text-xs text-[#C25934] font-bold">€{(p.price || 0).toFixed(2)}</p>
                            <p className="text-[10px] text-[#4A6B53]">📦 {availLabel}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E8E3D9] pt-3 flex gap-2">
              <Button
                data-testid="recovery-cancel"
                onClick={() => onOpenChange(false)}
                variant="outline"
                className="flex-1 h-11 rounded-full border-[#E8E3D9]"
              >
                Annulla
              </Button>
              <Button
                data-testid="recovery-migrate-btn"
                onClick={migrate}
                disabled={selected.size === 0 || migrating}
                className="flex-1 h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-semibold"
              >
                {migrating ? <Loader2 size={14} className="animate-spin" /> : `Sposta ${selected.size} nel volantino →`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
